import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, Check, ShieldCheck } from "lucide-react";
import { useApp } from "../context/AppContext";
import { tp } from "../i18n";

const LS_KEY = "fopfo_cookie_consent";
const SS_KEY = "fopfo_cookie_consent_session";
const REMEMBER_DAYS = 30;
const MS = REMEMBER_DAYS * 24 * 60 * 60 * 1000;

const CATEGORIES = [
  { key: "necessary", label: "Strictly necessary (essential)", locked: true,
    desc: "Required for the site to work: your secure sign-in session, security & anti-fraud protection, your language / theme preference, and remembering these cookie choices. These cannot be switched off." },
  { key: "functional", label: "Functional (optional)",
    desc: "Remember preferences such as your custom background colour, collapsed sidebar and last-used views to improve your experience." },
  { key: "analytics", label: "Analytics (optional)",
    desc: "Anonymous, aggregated usage measurement that helps us improve the product." },
  { key: "marketing", label: "Marketing (optional)",
    desc: "Used to measure and tailor promotional content." },
];

const DEFAULTS = { necessary: true, functional: false, analytics: false, marketing: false };

function applyConsent(choices) {
  window.__cookieConsent = choices;
  try { window.dispatchEvent(new CustomEvent("cookie-consent", { detail: choices })); } catch (e) { /* noop */ }
}

export default function CookieConsent() {
  const { lang } = useApp();
  const [open, setOpen] = useState(false);
  const [choices, setChoices] = useState(DEFAULTS);
  const [remember, setRemember] = useState(false);

  useEffect(() => {
    try {
      const ls = JSON.parse(localStorage.getItem(LS_KEY) || "null");
      if (ls && ls.ts && Date.now() - ls.ts < MS) { applyConsent(ls.choices); return; }
      if (ls) localStorage.removeItem(LS_KEY); // expired
      const ss = JSON.parse(sessionStorage.getItem(SS_KEY) || "null");
      if (ss && ss.choices) { applyConsent(ss.choices); return; }
    } catch (e) { /* fall through to show */ }
    // Auto-accept essentials for authenticated in-app routes so the banner never overlays
    // action buttons for logged-in users. They can still change choices under /privacy.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/app/")) {
      applyConsent(DEFAULTS);
      sessionStorage.setItem(SS_KEY, JSON.stringify({ choices: { ...DEFAULTS } }));
      return;
    }
    setOpen(true);
  }, []);

  const persist = (finalChoices) => {
    applyConsent(finalChoices);
    if (remember) {
      localStorage.setItem(LS_KEY, JSON.stringify({ choices: finalChoices, ts: Date.now() }));
      sessionStorage.removeItem(SS_KEY);
    } else {
      sessionStorage.setItem(SS_KEY, JSON.stringify({ choices: finalChoices }));
      localStorage.removeItem(LS_KEY);
    }
    setOpen(false);
  };

  const acceptAll = () => persist({ necessary: true, functional: true, analytics: true, marketing: true });
  const rejectNonEssential = () => persist({ ...DEFAULTS });
  const saveChoices = () => persist({ ...choices, necessary: true });

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4" data-testid="cookie-consent">
      <div className="mx-auto max-w-2xl rounded-xl border border-black/10 bg-white/95 p-4 shadow-2xl backdrop-blur-md dark:border-white/10 dark:bg-neutral-900/95 sm:p-5">
        <div className="flex items-center gap-2">
          <Cookie size={20} className="text-[var(--primary)]" />
          <h2 className="font-head text-lg">{tp("Your cookie choices", lang)}</h2>
        </div>
        <p className="mt-1 text-sm opacity-75">
          {tp("We use cookies and similar local storage. Choose which optional categories to allow — you can turn off everything except the strictly necessary ones. See our", lang)}{" "}
          <Link to="/cookies" className="font-medium text-[var(--primary)] hover:underline" data-testid="cookie-policy-link">{tp("Cookies Policy", lang)}</Link>.
        </p>

        <div className="mt-3 max-h-[38vh] space-y-2 overflow-auto pe-1">
          {CATEGORIES.map((c) => (
            <div key={c.key} className="flex items-start justify-between gap-3 rounded-lg border border-black/10 p-2.5 dark:border-white/10" data-testid={`cookie-cat-${c.key}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium">{tp(c.label, lang)}</div>
                <div className="mt-0.5 text-xs opacity-65">{tp(c.desc, lang)}</div>
              </div>
              {c.locked ? (
                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-1 text-[11px] font-medium text-emerald-700 dark:text-emerald-400" data-testid={`cookie-toggle-${c.key}`}>
                  <ShieldCheck size={12} /> {tp("Always on", lang)}
                </span>
              ) : (
                <button type="button" role="switch" aria-checked={choices[c.key]}
                  onClick={() => setChoices((p) => ({ ...p, [c.key]: !p[c.key] }))}
                  data-testid={`cookie-toggle-${c.key}`}
                  className={`relative h-6 w-11 shrink-0 rounded-full transition-colors ${choices[c.key] ? "bg-[var(--primary)]" : "bg-black/20 dark:bg-white/20"}`}>
                  <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${choices[c.key] ? "translate-x-[22px]" : "translate-x-0.5"}`} />
                </button>
              )}
            </div>
          ))}
        </div>

        <label className="mt-3 flex items-center gap-2 text-sm" data-testid="cookie-remember-label">
          <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="cookie-remember" />
          <span>{tp("Remember my choices for 30 days", lang)} <span className="opacity-60">{tp("(otherwise you'll see this again on your next visit).", lang)}</span></span>
        </label>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={rejectNonEssential} data-testid="cookie-reject"
            className="rounded-md border border-black/15 px-3 py-2 text-sm font-medium hover:bg-black/5 dark:border-white/15 dark:hover:bg-white/10">
            {tp("Reject non-essential", lang)}
          </button>
          <button type="button" onClick={saveChoices} data-testid="cookie-save"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--primary)] px-3 py-2 text-sm font-medium text-[var(--primary)] hover:bg-[var(--primary)]/10">
            <Check size={15} /> {tp("Save my choices", lang)}
          </button>
          <button type="button" onClick={acceptAll} data-testid="cookie-accept-all"
            className="rounded-md bg-[var(--primary)] px-3 py-2 text-sm font-semibold text-white hover:bg-[var(--primary-hover)]">
            {tp("Accept all", lang)}
          </button>
        </div>
      </div>
    </div>
  );
}
